/**
 * Filtros do Relatório: estado, aplicação e descrição — tudo puro.
 *
 * Regra única de combinação: dentro de um grupo vale OU (Medição OU
 * Executivo), entre grupos vale E (etapa E vendedor E período). A mesma
 * função filtra a tela e a exportação; `descreverFiltros` gera os chips da
 * barra e o rodapé do arquivo exportado, para o papel dizer o que a tela dizia.
 */
import { pontuar } from "../../utils/buscaTolerante";
import { formatarData } from "../../utils/calculos";
import { startOfDay, toDate } from "../../utils/status";
import {
  ETAPA_POR_ID,
  FASES,
  SITUACOES,
  ambienteNaEtapa,
  ambienteNaSituacao,
  etapasDaFase,
} from "./dominio";

export const SEM_VALOR = "(sem informação)";

const PERIODO_VAZIO = Object.freeze({ de: "", ate: "" });

export const FILTROS_PADRAO = Object.freeze({
  busca: "",
  etapas: [],
  situacoes: [],
  vendedores: [],
  consultores: [],
  marceneiros: [],
  projetistas: [],
  lotes: [],
  tipos: [],
  venda: PERIODO_VAZIO,
  entrega: PERIODO_VAZIO,
  carregamento: PERIODO_VAZIO,
  obraFutura: false,
});

/** Listas de valores: campo do filtro → como ler no ambiente. */
export const LISTAS = [
  { campo: "vendedores", label: "Vendedor", ler: (a) => a.vendedor },
  { campo: "consultores", label: "Consultor", ler: (a) => a.consultor },
  { campo: "marceneiros", label: "Marceneiro", ler: (a) => a.marceneiro },
  // Projetista do executivo: não vem da planilha, vem do backend (`useProjetistasExecutivo`).
  { campo: "projetistas", label: "Projetista", ler: (a) => a.projetista },
  { campo: "lotes", label: "Lote", ler: (a) => a.lote },
  { campo: "tipos", label: "Tipo de ambiente", ler: (a) => a.tipo },
];

export const PERIODOS = [
  { campo: "venda", label: "Data da venda", ler: (a) => a.venda },
  { campo: "entrega", label: "Entrega do contrato", ler: (a) => a.entregaContrato },
  { campo: "carregamento", label: "Carregamento previsto", ler: (a) => a.carregamentoPrevisto },
];

/** Estado vindo do armazenamento: completa o que falta e descarta o que não conhece. */
export function normalizarFiltros(bruto) {
  const base = { ...FILTROS_PADRAO };
  if (!bruto || typeof bruto !== "object") return base;
  const lista = (v) => (Array.isArray(v) ? v.filter((x) => typeof x === "string") : []);
  const periodo = (v) => ({
    de: typeof v?.de === "string" ? v.de : "",
    ate: typeof v?.ate === "string" ? v.ate : "",
  });
  return {
    busca: typeof bruto.busca === "string" ? bruto.busca : "",
    etapas: lista(bruto.etapas).filter((id) => ETAPA_POR_ID.has(id)),
    situacoes: lista(bruto.situacoes).filter((id) => SITUACOES.some((s) => s.id === id)),
    vendedores: lista(bruto.vendedores),
    consultores: lista(bruto.consultores),
    marceneiros: lista(bruto.marceneiros),
    projetistas: lista(bruto.projetistas),
    lotes: lista(bruto.lotes),
    tipos: lista(bruto.tipos),
    venda: periodo(bruto.venda),
    entrega: periodo(bruto.entrega),
    carregamento: periodo(bruto.carregamento),
    obraFutura: Boolean(bruto.obraFutura),
  };
}

function dentroDoPeriodo(data, periodo) {
  const de = toDate(periodo.de);
  const ate = toDate(periodo.ate);
  if (!de && !ate) return true;
  // Com período pedido, ambiente sem a data fica de fora: "sem data" não é "nesta semana".
  if (!data) return false;
  const dia = startOfDay(data).getTime();
  if (de && dia < startOfDay(de).getTime()) return false;
  if (ate && dia > startOfDay(ate).getTime()) return false;
  return true;
}

function textoDeBusca(ambiente) {
  return [ambiente.cliente, ambiente.pedido, ambiente.ambiente, ambiente.lote && `lote ${ambiente.lote}`]
    .filter(Boolean)
    .join(" ");
}

/** `ignorar`: campo deixado de fora — usado para contar as opções de um filtro sem ele mesmo. */
export function aplicarFiltros(ambientes, filtros, { ignorar = null } = {}) {
  const ativo = (campo) => campo !== ignorar;
  const busca = filtros.busca.trim();
  return ambientes.filter((ambiente) => {
    if (ativo("obraFutura") && !filtros.obraFutura && ambiente.obraFutura) return false;
    if (ativo("etapas") && filtros.etapas.length && !filtros.etapas.some((id) => ambienteNaEtapa(ambiente, id))) {
      return false;
    }
    if (
      ativo("situacoes") &&
      filtros.situacoes.length &&
      !filtros.situacoes.some((id) => ambienteNaSituacao(ambiente, id))
    ) {
      return false;
    }
    for (const { campo, ler } of LISTAS) {
      if (ativo(campo) && filtros[campo].length && !filtros[campo].includes(ler(ambiente) || SEM_VALOR)) return false;
    }
    for (const { campo, ler } of PERIODOS) {
      if (ativo(campo) && !dentroDoPeriodo(ler(ambiente), filtros[campo])) return false;
    }
    if (ativo("busca") && busca && pontuar(busca, textoDeBusca(ambiente)) <= 0) return false;
    return true;
  });
}

/**
 * Opções de uma lista com a contagem do que SOBRA aplicando os outros
 * filtros (o número ao lado da opção é o que ela traria). Opções já
 * escolhidas continuam na lista mesmo com zero, para poderem ser desmarcadas.
 */
export function opcoesDaLista(ambientes, filtros, campo) {
  const { ler } = LISTAS.find((l) => l.campo === campo);
  const universo = aplicarFiltros(ambientes, filtros, { ignorar: campo });
  const contagem = new Map();
  universo.forEach((ambiente) => {
    const valor = ler(ambiente) || SEM_VALOR;
    contagem.set(valor, (contagem.get(valor) || 0) + 1);
  });
  filtros[campo].forEach((valor) => {
    if (!contagem.has(valor)) contagem.set(valor, 0);
  });
  return [...contagem.entries()]
    .map(([valor, total]) => ({ valor, total }))
    .sort((a, b) => {
      if (a.valor === SEM_VALOR) return 1;
      if (b.valor === SEM_VALOR) return -1;
      return a.valor.localeCompare(b.valor, "pt-BR", { numeric: true });
    });
}

// ---------------------------------------------------------------------------
// Etapas ↔ fases
// ---------------------------------------------------------------------------

/** "todas" | "algumas" | "nenhuma" — estado do chip da fase a partir das etapas marcadas. */
export function estadoDaFase(filtros, faseId) {
  const ids = etapasDaFase(faseId).map((e) => e.id);
  const marcadas = ids.filter((id) => filtros.etapas.includes(id)).length;
  if (marcadas === 0) return "nenhuma";
  return marcadas === ids.length ? "todas" : "algumas";
}

/** Tocar na fase marca todas as etapas dela; tocar de novo desmarca todas. */
export function alternarFase(filtros, faseId) {
  const ids = etapasDaFase(faseId).map((e) => e.id);
  const semAFase = filtros.etapas.filter((id) => !ids.includes(id));
  return {
    ...filtros,
    etapas: estadoDaFase(filtros, faseId) === "todas" ? semAFase : [...semAFase, ...ids],
  };
}

export function alternarNaLista(filtros, campo, valor) {
  const atual = filtros[campo];
  return {
    ...filtros,
    [campo]: atual.includes(valor) ? atual.filter((v) => v !== valor) : [...atual, valor],
  };
}

// ---------------------------------------------------------------------------
// Períodos
// ---------------------------------------------------------------------------

function iso(data) {
  const d = startOfDay(data);
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mes}-${dia}`;
}

function somarDias(data, dias) {
  const d = startOfDay(data);
  d.setDate(d.getDate() + dias);
  return d;
}

/** Atalhos de período. Semana = segunda a domingo. */
export function atalhosDePeriodo(campo, hoje = new Date()) {
  const base = startOfDay(hoje);
  const diaDaSemana = base.getDay() === 0 ? 6 : base.getDay() - 1;
  const segunda = somarDias(base, -diaDaSemana);
  const inicioDoMes = new Date(base.getFullYear(), base.getMonth(), 1);
  const fimDoMes = new Date(base.getFullYear(), base.getMonth() + 1, 0);
  if (campo === "venda") {
    return [
      { id: "mes", label: "Este mês", de: iso(inicioDoMes), ate: iso(fimDoMes) },
      { id: "30", label: "Últimos 30 dias", de: iso(somarDias(base, -30)), ate: iso(base) },
      { id: "90", label: "Últimos 90 dias", de: iso(somarDias(base, -90)), ate: iso(base) },
    ];
  }
  return [
    { id: "vencidas", label: "Já passou", de: "", ate: iso(somarDias(base, -1)) },
    { id: "semana", label: "Esta semana", de: iso(segunda), ate: iso(somarDias(segunda, 6)) },
    { id: "mes", label: "Este mês", de: iso(inicioDoMes), ate: iso(fimDoMes) },
    { id: "30", label: "Próximos 30 dias", de: iso(base), ate: iso(somarDias(base, 30)) },
  ];
}

function descreverPeriodo(periodo) {
  const de = periodo.de ? formatarData(periodo.de) : "";
  const ate = periodo.ate ? formatarData(periodo.ate) : "";
  if (de && ate) return de === ate ? de : `${de} a ${ate}`;
  return de ? `a partir de ${de}` : `até ${ate}`;
}

// ---------------------------------------------------------------------------
// Descrição (chips e rodapé da exportação)
// ---------------------------------------------------------------------------

function descreverEtapas(filtros) {
  const partes = [];
  FASES.forEach((fase) => {
    const estado = estadoDaFase(filtros, fase.id);
    if (estado === "todas") {
      partes.push({ id: `fase:${fase.id}`, texto: fase.label, remover: (f) => alternarFase(f, fase.id) });
      return;
    }
    if (estado === "algumas") {
      etapasDaFase(fase.id)
        .filter((etapa) => filtros.etapas.includes(etapa.id))
        .forEach((etapa) =>
          partes.push({
            id: `etapa:${etapa.id}`,
            texto: etapa.label,
            remover: (f) => alternarNaLista(f, "etapas", etapa.id),
          })
        );
    }
  });
  return partes;
}

/**
 * `[{id, texto, remover(filtros) → filtros}]` — um item por filtro ativo.
 * A busca não entra: ela já está visível no próprio campo.
 */
export function descreverFiltros(filtros) {
  const itens = [...descreverEtapas(filtros)];
  filtros.situacoes.forEach((id) => {
    const situacao = SITUACOES.find((s) => s.id === id);
    itens.push({ id: `sit:${id}`, texto: situacao.label, remover: (f) => alternarNaLista(f, "situacoes", id) });
  });
  LISTAS.forEach(({ campo, label }) => {
    filtros[campo].forEach((valor) =>
      itens.push({
        id: `${campo}:${valor}`,
        texto: `${label}: ${valor}`,
        remover: (f) => alternarNaLista(f, campo, valor),
      })
    );
  });
  PERIODOS.forEach(({ campo, label }) => {
    const periodo = filtros[campo];
    if (!periodo.de && !periodo.ate) return;
    itens.push({
      id: `periodo:${campo}`,
      texto: `${label}: ${descreverPeriodo(periodo)}`,
      remover: (f) => ({ ...f, [campo]: PERIODO_VAZIO }),
    });
  });
  if (filtros.obraFutura) {
    itens.push({ id: "obraFutura", texto: "Inclui obra futura", remover: (f) => ({ ...f, obraFutura: false }) });
  }
  return itens;
}

/** Linhas para o rodapé do arquivo exportado (inclui a busca). */
export function filtrosParaExportacao(filtros) {
  const itens = descreverFiltros(filtros).map((item) => item.texto);
  if (filtros.busca.trim()) itens.unshift(`Busca: "${filtros.busca.trim()}"`);
  if (!filtros.obraFutura) itens.push("Sem obra futura");
  return itens;
}

export function limparFiltros(filtros) {
  return { ...FILTROS_PADRAO, busca: filtros.busca };
}
